import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Car } from "lucide-react";

const CLASSES = ["D1", "D2", "D3", "D4", "D5"];
const inp = "bg-black/40 border border-white/12 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#1F5EFF]";

const DEFAULT_BOOKING = {
  open_days: [0, 1, 2, 3, 4, 5],
  open_time: "09:00",
  close_time: "18:00",
  slot_step_min: 60,
  horizon_days: 30,
  min_lead_minutes: 120,
};

const DEFAULT_PROMO = {
  active: false,
  percent: 50,
  badge: "GRAND OPENING SPECIAL",
  headline: "50% OFF",
  subtext: "",
  limited: "LIMITED TIME • LIMITED SPOTS",
  bar_text: "",
  bar_cta: "BOOK NOW",
  services: [],
  service_labels: [],
  expires_at: null,
};

/** Owner/staff: vehicle-class pricing config + vehicle overrides. */
export default function PricingTab() {
  const [cfg, setCfg] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [{ data: c }, { data: o }] = await Promise.all([
      api.get("/crm/pricing"),
      api.get("/crm/vehicle-overrides"),
    ]);
    setCfg({ ...c, booking: c.booking || DEFAULT_BOOKING, promo: c.promo || DEFAULT_PROMO });
    setOverrides(o);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/crm/pricing", { services: cfg.services, addons: cfg.addons, booking: cfg.booking, promo: cfg.promo, bundles: cfg.bundles || [], upsell_rules: cfg.upsell_rules || [], followup_rules: cfg.followup_rules || [], ppf: cfg.ppf });
      setCfg({ ...data, booking: data.booking || DEFAULT_BOOKING, promo: data.promo || DEFAULT_PROMO });
      toast.success(`Pricing saved (v${data.version})`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const updatePpf = (fn) => setCfg({ ...cfg, ppf: fn(cfg.ppf) });
  const updatePpfPackage = (key, patch) =>
    updatePpf((ppf) => ({ ...ppf, packages: ppf.packages.map((p) => (p.key === key ? { ...p, ...patch } : p)) }));
  const updatePpfPart = (key, patch) =>
    updatePpf((ppf) => ({ ...ppf, parts: ppf.parts.map((p) => (p.key === key ? { ...p, ...patch } : p)) }));

  if (!cfg) return <div className="p-10 text-white/40"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  const updateLevel = (addonKey, levelKey, patch) =>
    setCfg({
      ...cfg,
      addons: {
        ...cfg.addons,
        [addonKey]: {
          ...cfg.addons[addonKey],
          levels: cfg.addons[addonKey].levels.map((l) => (l.key === levelKey ? { ...l, ...patch } : l)),
        },
      },
    });
  const updateAddon = (addonKey, patch) =>
    setCfg({ ...cfg, addons: { ...cfg.addons, [addonKey]: { ...cfg.addons[addonKey], ...patch } } });

  // price editor: scalar input or per-class grid (D1–D5)
  const priceEditor = (value, prices, onScalar, onClass, testidPrefix) => (
    prices ? (
      <div className="mt-2 grid grid-cols-5 gap-1.5">
        {CLASSES.map((c) => (
          <label key={c} className="block">
            <span className="text-[9px] font-mono-alt text-white/40">{c}</span>
            <input type="number" min="0" value={prices[c] ?? ""}
              onChange={(e) => onClass(c, e.target.value === "" ? null : Number(e.target.value))}
              className={`${inp} w-full mt-0.5 tabular-nums px-2 py-1 text-xs`} placeholder="—"
              data-testid={`${testidPrefix}-${c}`} />
          </label>
        ))}
      </div>
    ) : (
      <input type="number" min="0" value={value ?? 0}
        onChange={(e) => onScalar(Number(e.target.value) || 0)}
        className="w-20 bg-transparent text-white text-sm outline-none border-b border-white/10 focus:border-[#1F5EFF] tabular-nums"
        data-testid={testidPrefix} />
    )
  );

  return (
    <div data-testid="pricing-tab" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl text-white">Pricing Rules</h2>
          <p className="text-white/40 text-xs mt-1">
            Services and packages (per-class prices D1–D5) now live in <a href="/crm/services" className="text-[#8db2ff] hover:text-white underline underline-offset-2">Services →</a>.
            Here: add-ons, promo, booking rules, upsells and follow-ups. Version {cfg.version}.
          </p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-pricing"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F5EFF] text-white text-sm font-semibold hover:bg-white hover:text-black transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save All Changes
        </button>
      </div>


      {/* Add-on prices */}
      <section className="bg-[#121216] border border-white/12 rounded-2xl p-6" data-testid="pricing-addons">
        <div className="font-mono-alt text-[11px] text-[#1F5EFF] mb-4">Add-ons & Surcharges</div>
        {Object.keys(cfg.addons).map((ak) => {
          const a = cfg.addons[ak];
          return (
            <div key={ak} className="mb-5">
              <div className="text-white/70 text-sm mb-2">
                {a.label}
                <span className="text-white/30 text-[10px] ml-2">→ {(a.applies_to || []).join(", ")}</span>
              </div>
              {a.mode === "levels" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {a.levels.map((l) => (
                    <div key={l.key} className="bg-black/40 border border-white/12 rounded-xl p-3">
                      <div className="flex items-center gap-3">
                        <span className="flex-1 text-white/80 text-xs">{l.label}</span>
                        {l.custom ? (
                          <span className="text-amber-300/80 text-[11px]">Custom / Inspection</span>
                        ) : (
                          !l.prices && priceEditor(l.price, null,
                            (v) => updateLevel(ak, l.key, { price: v }), null,
                            `pricing-addon-${ak}-${l.key}`)
                        )}
                      </div>
                      {!l.custom && l.prices && priceEditor(null, l.prices, null,
                        (c, v) => updateLevel(ak, l.key, { prices: { ...l.prices, [c]: v } }),
                        `pricing-addon-${ak}-${l.key}`)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-black/40 border border-white/12 rounded-xl p-3 max-w-xl">
                  {a.service_ref ? (
                    <span className="text-white/50 text-xs">Priced from service: {a.service_ref}</span>
                  ) : (
                    priceEditor(a.price, a.prices,
                      (v) => updateAddon(ak, { price: v }),
                      (c, v) => updateAddon(ak, { prices: { ...a.prices, [c]: v } }),
                      `pricing-addon-${ak}`)
                  )}
                </div>
              )}
              {a.note && <div className="mt-1 text-[11px] text-white/30">{a.note}</div>}
            </div>
          );
        })}
      </section>

      <VehicleOverrides overrides={overrides} onChanged={load} />

      {/* PPF & Windshield film prices (owner-editable; GST 5% is statutory) */}
      {cfg.ppf && (
        <section className="bg-[#121216] border border-white/12 rounded-2xl p-6" data-testid="pricing-ppf">
          <div className="font-mono-alt text-[11px] text-[#1F5EFF] mb-1">PPF & Windshield Film</div>
          <p className="text-white/40 text-xs mb-5">
            CAD before GST (5% is added automatically on the site). PPF never joins the Grand Opening promo or package discounts.
          </p>

          <div className="text-white/70 text-sm mb-1">Packages — price by vehicle class (D1–D5)</div>
          <p className="text-white/30 text-[10px] mb-3">
            The site shows "From $D1" before the customer picks a vehicle, then the exact class price.
            {cfg.ppf.packages.some((p) => p.quote_only) && " Full Vehicle stays a custom quote after in-person assessment — its class prices are the starting figures we quote from."}
          </p>
          <div className="space-y-2 mb-6">
            {cfg.ppf.packages.map((p) => (
              <div key={p.key} className="bg-black/40 border border-white/12 rounded-xl p-3" data-testid={`ppf-pkg-${p.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex-1 text-white/80 text-xs">{p.name}</span>
                  {p.quote_only && <span className="text-amber-300/70 text-[10px]">custom quote — final price after assessment</span>}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                  {["D1", "D2", "D3", "D4", "D5"].map((c) => (
                    <label key={c} className="block">
                      <span className="text-[9px] font-mono-alt text-white/40">{c}</span>
                      <input type="number" min="0" value={p.prices?.[c] ?? ""}
                        onChange={(e) => updatePpfPackage(p.key, {
                          prices: { ...(p.prices || {}), [c]: e.target.value === "" ? null : Number(e.target.value) },
                        })}
                        className={`${inp} w-full mt-0.5 tabular-nums px-2 py-1 text-xs`}
                        data-testid={`ppf-pkg-price-${p.key}-${c}`} />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="text-white/70 text-sm mb-2">
            {cfg.ppf.windshield.name}
            <span className="text-white/30 text-[10px] ml-2">D5 / oversized glass → custom quote</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6 max-w-2xl">
            {["D1", "D2", "D3", "D4"].map((c) => (
              <label key={c} className="block">
                <span className="text-[9px] font-mono-alt text-white/40">{c}</span>
                <input type="number" min="0" value={cfg.ppf.windshield.prices?.[c] ?? ""}
                  onChange={(e) => updatePpf((ppf) => ({
                    ...ppf,
                    windshield: { ...ppf.windshield, prices: { ...ppf.windshield.prices, [c]: e.target.value === "" ? null : Number(e.target.value) } },
                  }))}
                  className={`${inp} w-full mt-0.5 tabular-nums`} placeholder="quote"
                  data-testid={`ppf-wpf-${c}`} />
              </label>
            ))}
          </div>

          <div className="text-white/70 text-sm mb-1">Individual panels</div>
          <p className="text-white/30 text-[10px] mb-2">
            Prices below are the D1 baseline. Large panels (bumpers, hood, fenders, roof, doors, quarter panels, rockers, trunk lid)
            scale automatically by vehicle class: D2 +10%, D3 +15%, D4 +20%, D5 +30% — rounded to the nearest $5. Small accessories keep one fixed price.
          </p>
          <div className="space-y-1.5">
            {cfg.ppf.parts.map((p) => (
              <div key={p.key} className="bg-black/40 border border-white/12 rounded-lg px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1.5"
                data-testid={`ppf-part-${p.key}`}>
                <span className="flex-1 min-w-[140px] text-white/80 text-xs">{p.label}</span>
                {p.kind === "flat" && (
                  <label className="flex items-center gap-1.5 text-[10px] text-white/40">
                    Price
                    <input type="number" min="0" value={p.price ?? 0}
                      onChange={(e) => updatePpfPart(p.key, { price: Number(e.target.value) || 0 })}
                      className={`${inp} w-20 tabular-nums px-2 py-1 text-xs`} data-testid={`ppf-part-price-${p.key}`} />
                  </label>
                )}
                {p.kind === "sided" && (
                  <>
                    <label className="flex items-center gap-1.5 text-[10px] text-white/40">
                      One side
                      <input type="number" min="0" value={p.single ?? 0}
                        onChange={(e) => updatePpfPart(p.key, { single: Number(e.target.value) || 0 })}
                        className={`${inp} w-20 tabular-nums px-2 py-1 text-xs`} data-testid={`ppf-part-single-${p.key}`} />
                    </label>
                    <label className="flex items-center gap-1.5 text-[10px] text-white/40">
                      Both
                      <input type="number" min="0" value={p.pair ?? 0}
                        onChange={(e) => updatePpfPart(p.key, { pair: Number(e.target.value) || 0 })}
                        className={`${inp} w-20 tabular-nums px-2 py-1 text-xs`} data-testid={`ppf-part-pair-${p.key}`} />
                    </label>
                  </>
                )}
                {p.kind === "count4" && (
                  <>
                    <label className="flex items-center gap-1.5 text-[10px] text-white/40">
                      Each
                      <input type="number" min="0" value={p.each ?? 0}
                        onChange={(e) => updatePpfPart(p.key, { each: Number(e.target.value) || 0 })}
                        className={`${inp} w-20 tabular-nums px-2 py-1 text-xs`} data-testid={`ppf-part-each-${p.key}`} />
                    </label>
                    {p.pair != null && (
                      <label className="flex items-center gap-1.5 text-[10px] text-white/40">
                        Pair
                        <input type="number" min="0" value={p.pair ?? 0}
                          onChange={(e) => updatePpfPart(p.key, { pair: Number(e.target.value) || 0 })}
                          className={`${inp} w-20 tabular-nums px-2 py-1 text-xs`} data-testid={`ppf-part-pair-${p.key}`} />
                      </label>
                    )}
                    <label className="flex items-center gap-1.5 text-[10px] text-white/40">
                      All 4
                      <input type="number" min="0" value={p.all4 ?? 0}
                        onChange={(e) => updatePpfPart(p.key, { all4: Number(e.target.value) || 0 })}
                        className={`${inp} w-20 tabular-nums px-2 py-1 text-xs`} data-testid={`ppf-part-all4-${p.key}`} />
                    </label>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>
      )}


      {/* Online booking settings */}
      <section className="bg-[#121216] border border-white/12 rounded-2xl p-6" data-testid="booking-settings">
        <div className="font-mono-alt text-[11px] text-[#1F5EFF] mb-1">Online Booking</div>
        <p className="text-white/40 text-xs mb-5">
          Customers pick real available slots in the website calculator. Each booking reserves one bay
          for the whole service duration. Capacity = number of active bays (CRM → Calendar → Bays).
        </p>
        <div className="flex flex-wrap gap-2 mb-5">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
            <button key={d} type="button"
              onClick={() => setCfg({
                ...cfg,
                booking: {
                  ...cfg.booking,
                  open_days: cfg.booking.open_days.includes(i)
                    ? cfg.booking.open_days.filter((x) => x !== i)
                    : [...cfg.booking.open_days, i],
                },
              })}
              data-testid={`booking-day-${i}`}
              className={`px-3.5 py-1.5 rounded-lg border text-xs transition-colors ${
                cfg.booking.open_days.includes(i)
                  ? "bg-[#1F5EFF] border-[#1F5EFF] text-white"
                  : "border-white/15 text-white/50 hover:border-white/30"}`}>
              {d}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <label><span className="text-[10px] font-mono-alt text-white/40 uppercase">Opens</span>
            <input type="time" value={cfg.booking.open_time}
              onChange={(e) => setCfg({ ...cfg, booking: { ...cfg.booking, open_time: e.target.value } })}
              className={`${inp} w-full mt-1`} data-testid="booking-open" /></label>
          <label><span className="text-[10px] font-mono-alt text-white/40 uppercase">Closes</span>
            <input type="time" value={cfg.booking.close_time}
              onChange={(e) => setCfg({ ...cfg, booking: { ...cfg.booking, close_time: e.target.value } })}
              className={`${inp} w-full mt-1`} data-testid="booking-close" /></label>
          <label><span className="text-[10px] font-mono-alt text-white/40 uppercase">Slot step (min)</span>
            <input type="number" min="15" step="15" value={cfg.booking.slot_step_min}
              onChange={(e) => setCfg({ ...cfg, booking: { ...cfg.booking, slot_step_min: Number(e.target.value) || 60 } })}
              className={`${inp} w-full mt-1`} data-testid="booking-step" /></label>
          <label><span className="text-[10px] font-mono-alt text-white/40 uppercase">Days ahead</span>
            <input type="number" min="1" max="90" value={cfg.booking.horizon_days}
              onChange={(e) => setCfg({ ...cfg, booking: { ...cfg.booking, horizon_days: Number(e.target.value) || 30 } })}
              className={`${inp} w-full mt-1`} data-testid="booking-horizon" /></label>
          <label><span className="text-[10px] font-mono-alt text-white/40 uppercase">Min lead (min)</span>
            <input type="number" min="0" step="30" value={cfg.booking.min_lead_minutes}
              onChange={(e) => setCfg({ ...cfg, booking: { ...cfg.booking, min_lead_minutes: Number(e.target.value) || 0 } })}
              className={`${inp} w-full mt-1`} data-testid="booking-lead" /></label>
        </div>
        <p className="text-white/30 text-[11px] mt-4">
          Last slot starts before closing time; jobs may run past closing. Same-day slots hide inside the min-lead window.
        </p>
      </section>

      {/* Grand Opening promotion */}
      <section className="bg-[#121216] border border-amber-400/25 rounded-2xl p-6" data-testid="promo-settings">
        <div className="flex items-center justify-between mb-1">
          <div className="font-mono-alt text-[11px] text-amber-300">Promotion — Grand Opening</div>
          <label className="flex items-center gap-2 text-xs text-white/70">
            <input type="checkbox" checked={!!cfg.promo?.active}
              onChange={(e) => setCfg({ ...cfg, promo: { ...cfg.promo, active: e.target.checked } })}
              className="w-4 h-4 accent-amber-400" data-testid="promo-active" />
            Enabled
          </label>
        </div>
        <p className="text-white/40 text-xs mb-5">
          One switch controls everything: the sticky announcement bar, the homepage section, crossed-out
          promo pricing and the discount in quotes/bookings. Regular prices are never modified.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <label><span className="text-[10px] font-mono-alt text-white/40 uppercase">Headline % (fallback if a service has no own %)</span>
            <input type="number" min="1" max="90" value={cfg.promo?.percent ?? 50}
              onChange={(e) => setCfg({ ...cfg, promo: { ...cfg.promo, percent: Number(e.target.value) || 50 } })}
              className={`${inp} w-full mt-1 tabular-nums`} data-testid="promo-percent" /></label>
          <label><span className="text-[10px] font-mono-alt text-white/40 uppercase">Additional services % (fallback)</span>
            <input type="number" min="1" max="90" value={cfg.promo?.multi_percent ?? 20}
              onChange={(e) => setCfg({ ...cfg, promo: { ...cfg.promo, multi_percent: Number(e.target.value) || 20 } })}
              className={`${inp} w-full mt-1 tabular-nums`} data-testid="promo-multi-percent" /></label>
          <label><span className="text-[10px] font-mono-alt text-white/40 uppercase">Badge text</span>
            <input value={cfg.promo?.badge || ""}
              onChange={(e) => setCfg({ ...cfg, promo: { ...cfg.promo, badge: e.target.value } })}
              className={`${inp} w-full mt-1`} data-testid="promo-badge-text" /></label>
          <label><span className="text-[10px] font-mono-alt text-white/40 uppercase">Headline</span>
            <input value={cfg.promo?.headline || ""}
              onChange={(e) => setCfg({ ...cfg, promo: { ...cfg.promo, headline: e.target.value } })}
              className={`${inp} w-full mt-1`} data-testid="promo-headline" /></label>
          <label><span className="text-[10px] font-mono-alt text-white/40 uppercase">Expires (optional)</span>
            <input type="date" value={cfg.promo?.expires_at || ""}
              onChange={(e) => setCfg({ ...cfg, promo: { ...cfg.promo, expires_at: e.target.value || null } })}
              className={`${inp} w-full mt-1`} data-testid="promo-expires" /></label>
        </div>
        <label className="block mb-4"><span className="text-[10px] font-mono-alt text-white/40 uppercase">Supporting text</span>
          <textarea rows={2} value={cfg.promo?.subtext || ""}
            onChange={(e) => setCfg({ ...cfg, promo: { ...cfg.promo, subtext: e.target.value } })}
            className={`${inp} w-full mt-1`} data-testid="promo-subtext" /></label>
        <label className="block mb-4"><span className="text-[10px] font-mono-alt text-white/40 uppercase">Announcement bar text</span>
          <input value={cfg.promo?.bar_text || ""}
            onChange={(e) => setCfg({ ...cfg, promo: { ...cfg.promo, bar_text: e.target.value } })}
            className={`${inp} w-full mt-1`} data-testid="promo-bar-text" /></label>
        <label className="block mb-4"><span className="text-[10px] font-mono-alt text-white/40 uppercase">Fine print (exclusions)</span>
          <input value={cfg.promo?.fineprint || ""}
            onChange={(e) => setCfg({ ...cfg, promo: { ...cfg.promo, fineprint: e.target.value } })}
            className={`${inp} w-full mt-1`} data-testid="promo-fineprint" /></label>
        <div className="mb-1 text-[10px] font-mono-alt text-white/40 uppercase">Eligible services (each gets its own % from the pricing config; the fallback % fields above apply only to a service without its own % — services only, never add-ons)</div>
        <div className="flex flex-wrap gap-2">
          {cfg.services.filter((s) => s.active).map((s) => {
            const on = (cfg.promo?.services || []).includes(s.key);
            return (
              <button key={s.key} type="button"
                onClick={() => setCfg({
                  ...cfg,
                  promo: {
                    ...cfg.promo,
                    services: on ? cfg.promo.services.filter((k) => k !== s.key) : [...(cfg.promo?.services || []), s.key],
                  },
                })}
                data-testid={`promo-service-${s.key}`}
                className={`px-3 py-1.5 rounded-lg border text-xs transition-colors ${
                  on ? "bg-amber-400/20 border-amber-400/50 text-amber-200" : "border-white/15 text-white/50 hover:border-white/30"}`}>
                {s.name}
              </button>
            );
          })}
        </div>
      </section>

      {/* Contextual upsell rules */}
      <section className="bg-[#121216] border border-white/12 rounded-2xl p-6" data-testid="upsell-rules-settings">
        <div className="font-mono-alt text-[11px] text-[#1F5EFF] mb-1">Contextual Upsells</div>
        <p className="text-white/40 text-xs mb-5">
          After a client picks a service, the calculator shows up to 3 relevant offers (the 1-Year Ceramic
          premium card counts as one for Paint Correction services). Add-on = attaches to the service;
          Service = added to the order as another service.
        </p>
        <div className="space-y-3">
          {(cfg.upsell_rules || []).map((r, ri) => {
            const svc = cfg.services.find((s) => s.key === r.service);
            if (!svc) return null;
            const setRule = (patch) => setCfg({
              ...cfg, upsell_rules: cfg.upsell_rules.map((x, xi) => xi === ri ? { ...x, ...patch } : x),
            });
            return (
              <div key={r.service} className="rounded-xl border border-white/10 p-3.5" data-testid={`upsell-rule-${r.service}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-white text-sm font-medium">{svc.name}</div>
                  <button type="button"
                    onClick={() => setCfg({ ...cfg, upsell_rules: cfg.upsell_rules.filter((_, xi) => xi !== ri) })}
                    className="p-1 text-white/35 hover:text-red-400" data-testid={`upsell-rule-del-${r.service}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(r.items || []).map((it, ii) => (
                    <div key={ii} className="flex items-center gap-2">
                      <select value={`${it.type}:${it.key}`}
                        onChange={(e) => {
                          const [type, key] = e.target.value.split(":");
                          setRule({ items: r.items.map((x, xi2) => xi2 === ii ? { type, key } : x) });
                        }}
                        className={`${inp} flex-1`} data-testid={`upsell-item-${r.service}-${ii}`}>
                        <optgroup label="Add-ons">
                          {Object.values(cfg.addons || {}).map((a) => (
                            <option key={a.key} value={`addon:${a.key}`}>+ {a.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Services">
                          {cfg.services.filter((s) => s.active).map((s) => (
                            <option key={s.key} value={`service:${s.key}`}>{s.name}</option>
                          ))}
                        </optgroup>
                      </select>
                      <button type="button"
                        onClick={() => setRule({ items: r.items.filter((_, xi2) => xi2 !== ii) })}
                        className="p-1.5 text-white/35 hover:text-red-400" data-testid={`upsell-item-del-${r.service}-${ii}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {(r.items || []).length < 3 && (
                    <button type="button"
                      onClick={() => setRule({ items: [...(r.items || []), { type: "addon", key: Object.keys(cfg.addons || {})[0] }] })}
                      className="text-[#8db2ff] text-xs hover:underline" data-testid={`upsell-item-add-${r.service}`}>
                      + Add offer ({(r.items || []).length}/3)
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <select value=""
            onChange={(e) => {
              const k = e.target.value;
              if (!k) return;
              setCfg({ ...cfg, upsell_rules: [...(cfg.upsell_rules || []), { service: k, items: [] }] });
            }}
            className={`${inp} w-full`} data-testid="upsell-rule-add">
            <option value="">+ Add upsell rule for a service…</option>
            {cfg.services.filter((s) => s.active && !(cfg.upsell_rules || []).some((r) => r.service === s.key))
              .map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </div>
      </section>

      {/* Follow-up rules — internal retention reminders */}
      <section className="bg-[#121216] border border-emerald-400/25 rounded-2xl p-6" data-testid="followup-rules-settings">
        <div className="font-mono-alt text-[11px] text-emerald-300 mb-1">Follow-Up Rules (Customer Retention)</div>
        <p className="text-white/40 text-xs mb-5">
          When an appointment with a matching service is completed, an internal reminder is created for the
          suggested service after the interval. Reminders live in CRM → Follow-Ups — nothing is ever sent
          to the customer automatically.
        </p>
        <div className="space-y-2.5">
          {(cfg.followup_rules || []).map((r, ri) => {
            const setRule = (patch) => setCfg({
              ...cfg, followup_rules: cfg.followup_rules.map((x, xi) => xi === ri ? { ...x, ...patch } : x),
            });
            return (
              <div key={ri} className="flex flex-wrap items-center gap-2.5 rounded-xl border border-white/10 p-3"
                data-testid={`frule-${r.service_key}`}>
                <input type="checkbox" checked={!!r.enabled}
                  onChange={(e) => setRule({ enabled: e.target.checked })}
                  className="w-3.5 h-3.5 accent-emerald-400" data-testid={`frule-enabled-${r.service_key}`} />
                <select value={r.service_key} onChange={(e) => setRule({ service_key: e.target.value })}
                  className={`${inp} flex-1 min-w-[150px]`} data-testid={`frule-service-${ri}`}>
                  {cfg.services.filter((s) => s.active).map((s) => (
                    <option key={s.key} value={s.key}>{s.name}</option>
                  ))}
                </select>
                <span className="text-white/40 text-xs">→ after</span>
                <input type="number" min="1" max="1095" value={r.after_days ?? 90}
                  onChange={(e) => setRule({ after_days: Number(e.target.value) || 90 })}
                  className={`${inp} w-20 tabular-nums`} data-testid={`frule-days-${r.service_key}`} />
                <span className="text-white/40 text-xs">days suggest</span>
                <select value={r.suggested_service_key} onChange={(e) => setRule({ suggested_service_key: e.target.value })}
                  className={`${inp} flex-1 min-w-[150px]`} data-testid={`frule-suggested-${r.service_key}`}>
                  {cfg.services.filter((s) => s.active).map((s) => (
                    <option key={s.key} value={s.key}>{s.name}</option>
                  ))}
                </select>
                <button type="button"
                  onClick={() => setCfg({ ...cfg, followup_rules: cfg.followup_rules.filter((_, xi) => xi !== ri) })}
                  className="p-1.5 text-white/35 hover:text-red-400" data-testid={`frule-del-${ri}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
          <button type="button"
            onClick={() => setCfg({
              ...cfg,
              followup_rules: [...(cfg.followup_rules || []),
                { service_key: "interior-detail", enabled: true, after_days: 90, suggested_service_key: "maintenance-detail" }],
            })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-400/40 text-emerald-300 text-xs hover:bg-emerald-400/10"
            data-testid="frule-add">
            <Plus className="w-3.5 h-3.5" /> Add rule
          </button>
        </div>
      </section>
    </div>
  );
}

function VehicleOverrides({ overrides, onChanged }) {
  const [f, setF] = useState({ make: "", model: "", year_from: "", year_to: "", vehicle_class: "D3", reason: "" });
  const [test, setTest] = useState({ year: "2024", make: "", model: "" });
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!f.make.trim() || !f.model.trim()) { toast.error("Make and model required"); return; }
    setBusy(true);
    try {
      await api.post("/crm/vehicle-overrides", {
        ...f,
        year_from: f.year_from ? Number(f.year_from) : null,
        year_to: f.year_to ? Number(f.year_to) : null,
      });
      toast.success("Override saved — applies to new quotes immediately");
      setF({ make: "", model: "", year_from: "", year_to: "", vehicle_class: "D3", reason: "" });
      onChanged();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/crm/vehicle-overrides/${id}`); onChanged(); }
    catch { toast.error("Delete failed"); }
  };

  const runTest = async () => {
    if (!test.make || !test.model) return;
    try {
      const { data } = await api.post("/vehicles/classify", { year: Number(test.year), make: test.make, model: test.model });
      setTestResult(data);
    } catch { toast.error("Classify failed"); }
  };

  return (
    <section className="bg-[#121216] border border-white/12 rounded-2xl p-6" data-testid="vehicle-overrides">
      <div className="flex items-center gap-2 mb-1">
        <Car className="w-4 h-4 text-[#1F5EFF]" />
        <div className="font-mono-alt text-[11px] text-[#1F5EFF]">Vehicle Class Overrides</div>
      </div>
      <p className="text-white/40 text-xs mb-5">
        Override the automatic classification for specific vehicles. Overrides win over the algorithm.
      </p>

      {/* Test classification */}
      <div className="flex flex-wrap items-center gap-2 mb-6 p-3 rounded-xl bg-black/40 border border-white/10">
        <input value={test.year} onChange={(e) => setTest({ ...test, year: e.target.value })} className={`${inp} w-20`} placeholder="Year" data-testid="ovr-test-year" />
        <input value={test.make} onChange={(e) => setTest({ ...test, make: e.target.value })} className={`${inp} w-36`} placeholder="Make" data-testid="ovr-test-make" />
        <input value={test.model} onChange={(e) => setTest({ ...test, model: e.target.value })} className={`${inp} w-44`} placeholder="Model" data-testid="ovr-test-model" />
        <button onClick={runTest} className="px-4 py-2 rounded-lg bg-white/10 text-white text-xs hover:bg-[#1F5EFF]" data-testid="ovr-test-run">Test</button>
        {testResult && (
          <span className="text-sm text-white/80" data-testid="ovr-test-result">
            → <strong className="text-white">{testResult.vehicle_class}</strong> <span className="text-white/40">({testResult.source})</span>
          </span>
        )}
      </div>

      {/* Add override */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-6">
        <input value={f.make} onChange={(e) => setF({ ...f, make: e.target.value })} className={inp} placeholder="Make (Ford)" data-testid="ovr-make" />
        <input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} className={inp} placeholder="Model (F-150)" data-testid="ovr-model" />
        <input value={f.year_from} onChange={(e) => setF({ ...f, year_from: e.target.value })} className={inp} placeholder="Year from" data-testid="ovr-year-from" />
        <input value={f.year_to} onChange={(e) => setF({ ...f, year_to: e.target.value })} className={inp} placeholder="Year to" data-testid="ovr-year-to" />
        <select value={f.vehicle_class} onChange={(e) => setF({ ...f, vehicle_class: e.target.value })} className={inp} data-testid="ovr-class">
          {["D1", "D2", "D3", "D4", "D5"].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={add} disabled={busy} className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-[#1F5EFF] text-white text-xs font-medium disabled:opacity-50" data-testid="ovr-add">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
        <input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={`${inp} col-span-2 md:col-span-6`} placeholder="Reason (optional, e.g. 'SuperCrew is always XL')" data-testid="ovr-reason" />
      </div>

      {/* List */}
      <div className="space-y-2">
        {overrides.length === 0 && <div className="text-white/30 text-sm italic">No overrides yet.</div>}
        {overrides.map((o) => (
          <div key={o.id} className={`flex items-center gap-3 p-3 rounded-xl border ${o.active ? "bg-white/5 border-white/10" : "bg-white/2 border-white/5 opacity-50"}`}
            data-testid={`override-${o.id}`}>
            <span className="text-white text-sm flex-1">
              {o.make} {o.model}
              <span className="text-white/40 text-xs ml-2">
                {o.year_from || o.year_to ? `${o.year_from || "…"}–${o.year_to || "…"}` : "all years"}
              </span>
              {o.reason && <span className="text-white/30 text-xs ml-2">· {o.reason}</span>}
            </span>
            <span className="px-2 py-0.5 rounded bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 text-[#8db2ff] text-xs font-mono-alt">{o.vehicle_class}</span>
            <button onClick={async () => { await api.patch(`/crm/vehicle-overrides/${o.id}`, { active: !o.active }); onChanged(); }}
              className="text-white/40 hover:text-white text-[11px] px-2" data-testid={`override-toggle-${o.id}`}>
              {o.active ? "Disable" : "Enable"}
            </button>
            <button onClick={() => remove(o.id)} className="p-1.5 text-white/40 hover:text-red-400" data-testid={`override-del-${o.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
